import { Component, Input, OnInit, OnDestroy } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Idioma } from "@sharedV11/classes/maestros/idiomas/idioma";
import { HttpErrorsService } from "@sharedV11/services/alerts/errors/http-errors.service";
import { SnackbarInfoService } from "@sharedV11/services/alerts/snackbar-info/snackbar-info.service";
import { IdiomasService } from "@sharedV11/services/components/Idiomas/idiomas.service";
import { PanelService } from "@sharedV11/services/panel/panel.service";
import { Translator } from "@sharedV11/services/translator/translator.service";
import { HttpClient } from "@angular/common/http";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { deepMerge } from "@sharedV11/utils/deep-merge";

declare const require: any; // Para cargar imágenes estáticas desde assets

interface TraduccionItem {
  clave: string;
  valor: string;
  originalValor: string;
  esSeparador?: boolean; // Indica si es un separador/subcategoría
  nombreSubcategoria?: string; // Nombre de la subcategoría
  esSubcategoriaPrincipal?: boolean; // Flag para estilo de categoria principal
  esSubcategoriaAninhada?: boolean; // Flag para estilo de subcategoria aninhada (dentro de outra)
}

interface FotoEvidencia {
  id: string;
  nombre: string;
  url: string; // Base64 ou URL da imagem
  file?: File; // Arquivo original (opcional)
  fechaSubida: Date;
}

interface SeccionTraducciones {
  nombre: string;
  expandida: boolean;
  traducciones: TraduccionItem[];
  fotos?: FotoEvidencia[]; // Fotos de evidência para esta sección
}

@Component({
  selector: "app-edit-idiomas",
  templateUrl: "./edit-idiomas.component.html",
  styleUrls: ["./edit-idiomas.component.scss"],
})
export class EditIdiomasComponent implements OnInit, OnDestroy {
  loading = true;
  private ngUnsubscribe = new Subject();

  @Input() pkId: string;
  @Input() mode: string;
  @Input() modificar: boolean;

  translation: any;
  idioma: Idioma;
  nombreIdioma: string = "";
  codigoIdioma: string = "";

  // Secciones del accordion
  secciones: SeccionTraducciones[] = [];

  // Control de expansão de subcategorias (chave: nome da subcategoria, valor: boolean)
  subcategoriasExpandidas: Map<string, boolean> = new Map();

  // Control de cambios
  hayCambios = false;

  // Mapa para armazenar fotos por sección (chave: nome da seção)
  fotosPorSeccion: Map<string, FotoEvidencia[]> = new Map();

  // Preview de imagem em modal
  previewVisible = false;
  previewUrl = "";
  previewNombre = "";
  zoomLevel = 1;
  readonly zoomMin = 0.5;
  readonly zoomMax = 4;
  readonly zoomStep = 0.2;
  panX = 0;
  panY = 0;
  panStartX = 0;
  panStartY = 0;
  panStartClientX = 0;
  panStartClientY = 0;
  isPanning = false;

  // Modal de guía visual (galería por sección)
  guiaVisible = false;
  guiaSeccion: SeccionTraducciones | null = null;
  guiaWasOpen = false;
  previewLista: FotoEvidencia[] = [];
  previewIndex = 0;
  sourceTranslates: null | {} = null;

  constructor(
    private idiomasService: IdiomasService,
    private fb: FormBuilder,
    private translator: Translator,
    private panelService: PanelService,
    private snackvar_service: SnackbarInfoService,
    private httpErrorService: HttpErrorsService,
    private http: HttpClient
  ) {}

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Carga imágenes estáticas desde assets/img-idiomas por sección.
   * Mapeo explícito de carpetas:
   *  - Página de inicio (Home)        -> home
   *  - Login y registro               -> loginregistro
   *  - Pasos de la Compra (Checkout)  -> pasosdelacompra
   *  - Resumen del carrito            -> resumendelcarrito
   *  - Formulario de compra           -> formulariodecompra
   *  - Página de gracias (Thankyou)   -> paginadegracias
   */
  private cargarImagenesEstaticas(): void {
    const mapeo: Record<string, string> = {
      'Página de inicio (Home)': 'home',
      'Login y registro': 'loginyregistro',
      'Pasos de la Compra (Checkout)': 'pasosdelacompra',
      'Resumen del carrito': 'resumendelcarrito',
      'Formulario de compra': 'formulariodecompra',
      'Página de gracias (Thankyou)': 'paginadegracias'
    };

    Object.keys(mapeo).forEach(nombreSeccion => {
      const carpeta = mapeo[nombreSeccion];
      const fotos = this.cargarImagenesDeCarpeta(carpeta);
      if (fotos.length > 0) {
        this.fotosPorSeccion.set(nombreSeccion, fotos);
        const seccion = this.secciones.find(s => s.nombre === nombreSeccion);
        if (seccion) {
          seccion.fotos = fotos;
        }
      }
    });
  }

  /**
   * Utiliza require.context para obtener todas las imágenes de una carpeta.
   * Acepta png, jpg, jpeg, gif, webp y svg.
   */
  private cargarImagenesDeCarpeta(carpeta: string): FotoEvidencia[] {
    const fotos: FotoEvidencia[] = [];
    const context = this.obtenerContextoCarpeta(carpeta);
    if (!context) {
      return fotos;
    }
    try {
      context.keys().forEach((key: string, idx: number) => {
        const mod = context(key);
        const url = mod && mod.default ? mod.default : mod; // manejar esModule true/false
        const nombre = key.replace('./', '');
        fotos.push({
          id: `${carpeta}_${idx}`,
          nombre,
          url,
          fechaSubida: new Date()
        });
      });
    } catch (error) {
      console.warn('[TraduccionesComponent] No se pudieron cargar imágenes estáticas de', carpeta, error);
    }
    return fotos;
  }

  /**
   * Devuelve el require.context específico para cada carpeta (debe ser literal para webpack).
   */
  private obtenerContextoCarpeta(carpeta: string): any | null {
    try {
      switch (carpeta) {
        case 'home':
          return (require as any).context('../../../../../assets/img-idiomas/home', false, /\.(png|jpe?g|gif|webp|svg)$/);
        case 'loginyregistro':
          return (require as any).context('../../../../../assets/img-idiomas/loginyregistro', false, /\.(png|jpe?g|gif|webp|svg)$/);
        case 'pasosdelacompra':
          return (require as any).context('../../../../../assets/img-idiomas/pasosdelacompra', false, /\.(png|jpe?g|gif|webp|svg)$/);
        case 'resumendelcarrito':
          return (require as any).context('../../../../../assets/img-idiomas/resumendelcarrito', false, /\.(png|jpe?g|gif|webp|svg)$/);
        case 'formulariodecompra':
          return (require as any).context('../../../../../assets/img-idiomas/formulariodecompra', false, /\.(png|jpe?g|gif|webp|svg)$/);
        case 'paginadegracias':
          return (require as any).context('../../../../../assets/img-idiomas/paginadegracias', false, /\.(png|jpe?g|gif|webp|svg)$/);
        default:
          return null;
      }
    } catch (error) {
      console.warn('[TraduccionesComponent] No se pudo crear contexto para carpeta', carpeta, error);
      return null;
    }
  }

  ngOnInit(): void {
    this.initializeTranslate();
    this.loadIdiomaData(); // Cargar datos reales desde la API
  }

  async   initializeTranslate() {
    this.translation = this.translator.GetTranslations();
    // Suscribirse a cambios de idioma
    this.translator.dataLanguage$.subscribe((data) => {
      if (data) {
        this.translation = this.translator.GetTranslations();
      }
    });
  }

  // Método temporal para cargar datos mock (solo para visualización)
  loadMockData() {
    // console.log('[TraduccionesComponent] Cargando datos MOCK para pruebas visuales');
    
    // Simular datos del idioma
    this.nombreIdioma = 'Español (Mock)';
    this.codigoIdioma = 'es-ES';
    
    // Crear traducciones de ejemplo
    const mockTranslations = {
      // ═══════════════════════════════════════════════════
      // HOME - Solo los campos de las fotos
      // ═══════════════════════════════════════════════════
      
      // General
      'home.logoUrl': 'logo.svg',
      'home.inicio': 'Inicio',
      
      // Banner
      'home.banner.title': 'Disfruta del nuevo espacio para comprar tickets',
      'home.banner.subtitle': 'Visita lugares increíbles y conoce más sobre su historia.',
      
      // Cards
      'home.cards.more': 'Ver más',
      'home.cards.readmore': 'Leer más',
      'home.cards.textPrice': 'Precio',
      'home.cards.action': 'Comprar ahora',
      
      // Footer - Links
      'home.footer.configCookies': 'Configurar cookies',
      'home.footer.politicaCompras': 'Política de compras',
      'home.footer.linkPoliticaCompras': '/politica-compras',
      'home.footer.avisoLegal': 'Aviso legal',
      'home.footer.linkAvisoLegal': '/aviso-legal',
      'home.footer.politicaCookies': 'Política de cookies',
      'home.footer.linkPoliticaCookies': '/politica-cookies',
      'home.footer.politicaPrivacidad': 'Política de privacidad',
      'home.footer.linkPoliticaPrivacidad': '/politica-privacidad',
      
      // Footer - Cookies Modal
      'home.footer.modalTitle': 'Configuración de privacidad',
      'cookies.msgUsage': 'Este sitio web utiliza cookies para mejorar su experiencia de navegación.',
      'cookies.actions.accept': 'Aceptar todas',
      'cookies.actions.config': 'Configurar cookies',
      
      // ═══════════════════════════════════════════════════
      // LOGIN Y REGISTRO - Solo los campos de las fotos
      // ═══════════════════════════════════════════════════
      
      // Login - Iniciar Sesión
      'login.action': 'Entrar',
      'login.title': 'Iniciar sesión',
      'login.usuario': 'Usuario',
      'login.password': 'Contraseña',
      'login.default.action': 'Iniciar sesión',
      'login.forgotPassword.link': '¿Olvidaste tu contraseña?',
      'login.default.registro': 'Registrarse',
      
      // Login - Recuperar Contraseña
      'login.forgotPassword.title': 'Recuperar contraseña',
      'login.forgotPassword.hint': 'Ingresa tu correo electrónico para recuperar tu contraseña',
      'login.forgotPassword.label': 'Correo electrónico',
      'login.forgotPassword.action': 'Enviar',
      
      // Login - Cambiar Contraseña
      'login.changePassword.title': 'Cambiar contraseña',
      'login.changePassword.hint': 'Ingresa tu nueva contraseña',
      'login.changePassword.newpassword': 'Nueva contraseña',
      'login.changePassword.newpasswordcopy': 'Confirmar nueva contraseña',
      'login.changePassword.action': 'Cambiar contraseña',

      // Registro - Datos del usuario
      'login.register.title': 'Registrar nuevo usuario',
      'login.register.fases.FASE1': 'Datos del usuario',
      'login.register.fields.nombre': 'Nombre',
      'login.register.fields.razonsocial': 'Razón social',
      'login.register.fields.nombrecomercial': 'Nombre comercial',
      'login.register.fields.pais': 'País',
      'login.register.fields.localidad': 'Localidad',
      'login.register.fields.tipoDocumento': 'Tipo de documento',
      'login.register.fields.email': 'Correo electrónico',
      'login.register.fields.password': 'Contraseña',
      'login.register.fields.apellidos': 'Apellidos',
      'login.register.fields.direccion': 'Dirección',
      'login.register.fields.nombreresponsable': 'Nombre del responsable',
      'login.register.fields.provincia': 'Provincia',
      'login.register.fields.codigoPostal': 'Código postal',
      'login.register.fields.documento': 'Documento',
      'login.register.fields.emailconfirmacion': 'Confirmar correo',
      'login.register.fields.passwordconfirmacion': 'Confirmar contraseña',
      'login.register.actions.bill': 'Facturar',
      // Registro - Tipo de mercado y procedencia (nueva subcategoría)
      'login.register.fases.FASE2': 'Tipo de mercado y procedencia',
      'login.register.fields.mercado': 'Mercado objetivo',
      'login.register.fields.procedencia': 'Procedencia del negocio',
      // Registro - Datos adicionales (nueva subcategoría)
      'login.register.fases.FASE3': 'Datos adicionales',
      'login.register.fields.cargo': 'Cargo',
      'login.register.fields.turismo': 'Turismo',
      'login.register.fields.operador': 'Operador',
      'login.register.fields.idiomaAdicional': 'Idioma adicional',
      // Registro - Información de protección de datos (nueva subcategoría)
      'login.register.fases.informacion': 'Información de protección de datos',
      'login.register.fields.textoLegal': 'Texto legal',
      'login.register.fields.privacidad': 'Privacidad',
      'login.register.fields.publicidad': 'Publicidad',
      // Checkout - Registros de visitas (nueva subcategoría en Pasos de la Compra)
      'step.date.title': 'Fecha',
      'step.date.subtitle': 'Seleccione la fecha deseada',
      'step.tickets.title': 'Entradas',
      'step.tickets.subtitle': 'Seleccione sus entradas',
      'step.hour.title': 'Horario',
      'step.hour.subtitle': 'Seleccione un horario',
      'step.date.mainTitle': 'Calendario principal',
      'step.date.state.available': 'Disponible',
      'step.date.state.soldout': 'Agotado',
      'step.date.state.last': 'Últimas',
      'step.date.state.close': 'Cerrado',
      'step.date.calendarioEtiqueta_1': 'Etiqueta calendario 1',
      'step.date.calendarioEtiqueta_2': 'Etiqueta calendario 2',
      'step.navigation.next': 'Siguiente',
      'step.tickets.mainTitle': 'Título principal de entradas',
      'step.navigation.previous': 'Anterior',
      'step.hour.mainTitle': 'Título principal de horario',
      // Checkout - Tiempo
      'timer.title': 'Tiempo restante',
      
      // Checkout
      'checkout.title': 'Finalizar compra',
      'checkout.step1': 'Carrito',
      'checkout.step2': 'Datos personales',
      'checkout.step3': 'Pago',
      'checkout.step4': 'Confirmación',
      'payment.method.title': 'Método de pago',
      'payment.method.card': 'Tarjeta de crédito',
      'payment.method.paypal': 'PayPal',
      'cart.empty': 'Tu carrito está vacío',
      'cart.total': 'Total',
      'cart.continue': 'Continuar comprando',
      
      // Cart Summary
      'cart.resume.title': 'Resumen del carrito',
      'cart.resume.subtitle': 'Subtítulo del resumen',
      'cart.resume.card.actions.buyMore': 'Comprar más',
      'cart.resume.card.actions.edit': 'Editar',
      'cart.resume.card.actions.delete': 'Eliminar',
      'cart.resume.actions.next': 'Siguiente',
      'cart.resume.actions.more': 'Más',
      'cart.resume.titleSuggest': 'Título sugerido',
      
      // Form - Datos del comprador
      'data.banner': 'Banner',
      'data.title': 'Título',
      'data.datosCliente': 'Datos del cliente',
      'data.fieldsClient.Nombrecontacto': 'Nombre de contacto',
      'data.fieldsClient.nombre': 'Nombres',
      'data.fieldsClient.apellido1': 'Apellido 1',
      'data.fieldsClient.nombreresponsable': 'Nombre responsable',
      'data.fieldsClient.documento': 'Documento',
      'data.fieldsClient.emailconfirmacion': 'Email de confirmación',
      'data.fieldsClient.telefono': 'Teléfono',
      'data.fieldsClient.localidad': 'Localidad',
      'data.fieldsClient.codigoPostal': 'Código postal',
      'data.fieldsClient.nombrecentro': 'Nombre del centro',
      'data.fieldsClient.notas': 'Notas',
      'data.fieldsClient.notasinternas': 'Notas internas',
      'data.fieldsClient.nombreAdicional': 'Nombre adicional',
      'data.fieldsClient.apellidos': 'Apellidos',
      'data.fieldsClient.Apellido2Contacto': 'Apellido 2 contacto',
      'data.fieldsClient.tipoDocumento': 'Tipo de documento',
      'data.fieldsClient.email': 'Email',
      'data.fieldsClient.telefonomovil': 'Teléfono móvil',
      'data.fieldsClient.direccion': 'Dirección',
      'data.fieldsClient.provincia': 'Provincia',
      'data.fieldsClient.pais': 'País',
      'data.fieldsClient.notasExternas': 'Notas externas',
      'data.bill': 'Necesitaré factura',
      'data.codigoPromocionalTitle': 'Título código promocional',
      'data.codigoPromocionalMsg': 'Mensaje código promocional',
      'data.codigoPromocionalAction': 'Acción código promocional',
      'data.monedero.action': 'Acción monedero',
      'data.monedero.tarjetaMonedero': 'Tarjeta monedero',
      'data.monedero.monederoMsg': 'Mensaje monedero',
      'data.seleccionaPago': 'Selecciona pago',
      'data.fieldsClient.textoLegal': 'Texto legal',
      'data.fieldsClient.privacidad': 'Privacidad',
      'data.fieldsClient.publicidad': 'Publicidad',
      
      // Thank You - Resumen de la compra
      'pay.banner': 'Banner',
      'pay.title': 'Título',
      'pay.textSend': 'Texto enviar',
      'pay.actions.webExternal': 'Web externa',
      'pay.share': 'Compartir',
      'pay.actions.buy': 'Comprar',
      'pay.linkInstagram': 'Link Instagram',
      
      // Otros
      'common.accept': 'Aceptar',
      'common.cancel': 'Cancelar',
      'common.save': 'Guardar',
      'common.edit': 'Editar',
      'common.delete': 'Eliminar',
      'common.close': 'Cerrar',
      'error.generic': 'Ha ocurrido un error',
      'error.network': 'Error de conexión',
      'success.saved': 'Guardado correctamente',
    };
    
    this.organizarTraduccionesPorSeccion(mockTranslations);
    // Cargar imágenes estáticas desde assets/img-idiomas
    this.cargarImagenesEstaticas();
    this.loading = false;
    
    // console.log('[TraduccionesComponent] Datos MOCK cargados exitosamente');
  }

  /**
   * Carga los datos del idioma desde la API
   */
  loadIdiomaData() {
    if (!this.pkId) {
      // console.warn('[TraduccionesComponent] No hay pkId, usando datos mock para pruebas');
      // Si no hay pkId, usar datos mock temporales para desarrollo/pruebas
      this.loadMockData();
      return;
    }

    // console.log(`[TraduccionesComponent] Cargando datos del idioma con pkId: ${this.pkId}`);
    
    this.idiomasService
      .findIdiomaByPkId(this.pkId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (response) => {
          // console.log('[TraduccionesComponent] Respuesta de API recibida:', response);
          // console.log('[TraduccionesComponent] Estructura DatosResult:', response?.DatosResult);
          // console.log('[TraduccionesComponent] lista:', response?.DatosResult?.lista);
          
          // Intentar diferentes estructuras de respuesta
          let idioma = response?.DatosResult?.lista?.[0];
          
          // Si no está en lista, buscar en listaIdiomas directamente
          if (!idioma && response?.listaIdiomas) {
            // console.log('[TraduccionesComponent] Buscando en listaIdiomas directamente');
            idioma = response.listaIdiomas.find((i: any) => i.pkId.toString() === this.pkId);
          }
          
          // Si aún no está, buscar en DatosResult.listaIdiomas
          if (!idioma && response?.DatosResult?.listaIdiomas) {
            // console.log('[TraduccionesComponent] Buscando en DatosResult.listaIdiomas');
            idioma = response.DatosResult.listaIdiomas.find((i: any) => i.pkId.toString() === this.pkId);
          }
          
          if (idioma) {
            // console.log('[TraduccionesComponent] Idioma encontrado:', idioma);
            this.idioma = idioma;
            this.nombreIdioma = idioma.nombre;
            this.codigoIdioma = idioma.codIdioma;
            this.loadTranslations(idioma.codIdioma);
          } else {
            console.warn('[TraduccionesComponent] No se encontró el idioma en la respuesta');
            console.warn('[TraduccionesComponent] pkId buscado:', this.pkId);
            console.warn('[TraduccionesComponent] Respuesta completa:', JSON.stringify(response, null, 2));
            this.loading = false;
          }
        },
        (error) => {
          console.error('[TraduccionesComponent] Error al cargar idioma:', error);
          this.httpErrorService.identificarErrores(error);
          this.loading = false;
        }
      );
  }

  /**
   * Carga las traducciones desde la API GetJsonRecursosIdiomas
   */
  loadTranslations(codigoIdioma: string) {
    // console.log(`[TraduccionesComponent] Cargando traducciones para idioma: ${codigoIdioma}`);
    
    this.idiomasService
      .getJsonRecursosIdiomas(codigoIdioma)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (response) => {
          // console.log('[TraduccionesComponent] Respuesta completa de API:', JSON.stringify(response, null, 2));
          
          if (response?.DatosResult?.Contenido) {
            // La API devuelve un objeto anidado, necesitamos aplanarlo
            const contenido = response.DatosResult.Contenido;
            this.sourceTranslates = contenido;
            // console.log('[TraduccionesComponent] Contenido recibido (antes de aplanar):', contenido);
            
            const translations = this.aplanarObjeto(contenido);
            // console.log(`[TraduccionesComponent] Traducciones aplanadas. Cantidad de claves:`, Object.keys(translations).length);
            // console.log('[TraduccionesComponent] Primeras 10 claves:', Object.keys(translations).slice(0, 10));
            
            if (Object.keys(translations).length === 0) {
              console.warn('[TraduccionesComponent] No se encontraron traducciones después de aplanar el objeto');
              this.snackvar_service.openSnackBar(
                this.translator.instant('editIdiomas.errorNoTraduccionesAPI') || 'No se encontraron traducciones en la respuesta de la API',
                "",
                "red-snackbar"
              );
              this.secciones = [];
              this.loading = false;
              return;
            }
            
            this.organizarTraduccionesPorSeccion(translations);
            this.cargarImagenesEstaticas();
            this.loading = false;
          } else if (response?.Mensajes && response.Mensajes.length > 0) {
            // Error en la respuesta
            const mensaje = response.Mensajes[0].DescripcionMensaje;
            console.error('[TraduccionesComponent] Error en la respuesta:', mensaje);
            this.snackvar_service.openSnackBar(
              mensaje,
              "",
              "red-snackbar"
            );
            this.secciones = [];
            this.loading = false;
          } else {
            console.warn('[TraduccionesComponent] No se encontraron traducciones en la respuesta. Estructura:', response);
            this.snackvar_service.openSnackBar(
              this.translator.instant('editIdiomas.errorAPINoTraducciones') || 'La API no devolvió traducciones para este idioma',
              "",
              "red-snackbar"
            );
            this.secciones = [];
            this.loading = false;
          }
        },
        (error) => {
          console.error('[TraduccionesComponent] Error al cargar traducciones:', error);
          this.httpErrorService.identificarErrores(error);
          this.snackvar_service.openSnackBar(
            this.translator.instant('editIdiomas.errorCargarTraducciones') || 'Error al cargar las traducciones desde la API',
            "",
            "red-snackbar"
          );
          this.secciones = [];
          this.loading = false;
        }
      );
  }

  /**
   * Aplana un objeto anidado en un objeto plano con claves con puntos
   * Ejemplo: { home: { banner: { title: "..." } } } -> { "home.banner.title": "..." }
   * Maneja objetos, arrays y valores primitivos
   */
  private aplanarObjeto(obj: any, prefix: string = '', result: any = {}): any {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const newKey = prefix ? `${prefix}.${key}` : key;
        const value = obj[key];
        
        if (value === null || value === undefined) {
          // Valores null o undefined se agregan como están
          result[newKey] = value;
        } else if (Array.isArray(value)) {
          // Si es un array, agregarlo directamente (no aplanamos arrays)
          result[newKey] = value;
        } else if (typeof value === 'object') {
          // Si es un objeto, aplanarlo recursivamente
          this.aplanarObjeto(value, newKey, result);
        } else {
          // Si es un valor primitivo (string, number, boolean), agregarlo directamente
          result[newKey] = value;
        }
      }
    }
    return result;
  }

  organizarTraduccionesPorSeccion(translations: any) {
    // console.log('[TraduccionesComponent] Organizando traducciones. Total de claves:', Object.keys(translations).length);
    
    // Organizar traducciones por secciones basándose en prefijos de claves
    const seccionesMap = new Map<string, TraduccionItem[]>();

    // Definir secciones y sus prefijos (Home inicia abierta por pedido)
    const definicionSecciones = [
      { nombre: 'Página de inicio (Home)', prefijos: ['home.', 'cookie.', 'cookies.'], expandida: true },
      { nombre: 'Login y registro', prefijos: ['login.', 'register.', 'password.'], expandida: false },
      { nombre: 'Pasos de la Compra (Checkout)', prefijos: ['checkout.', 'payment.', 'step.', 'timer.'], expandida: false },
      { nombre: 'Resumen del carrito', prefijos: ['cart.resume.'], expandida: false },
      { nombre: 'Formulario de compra', prefijos: ['data.'], expandida: false },
      { nombre: 'Página de gracias (Thankyou)', prefijos: ['pay.'], expandida: false }
    ];
    
    // Definir campos gerais de Home (antes do separador Cookies)
    const camposHome = ['home.logoUrl', 'home.inicio', 'home.banner.title', 'home.banner.subtitle', 'home.cards.more', 'home.cards.readmore', 'home.cards.textPrice', 'home.cards.action', 'home.footer.configCookies', 'home.footer.politicaCompras', 'home.footer.linkPoliticaCompras', 'home.footer.avisoLegal', 'home.footer.linkAvisoLegal', 'home.footer.politicaCookies', 'home.footer.linkPoliticaCookies', 'home.footer.politicaPrivacidad', 'home.footer.linkPoliticaPrivacidad'];
    
    // Definir subcategorías de cookies dentro de Home
    const camposCookies = ['home.footer.modalTitle', 'cookies.msgUsage', 'cookies.actions.accept', 'cookies.actions.config'];
    
    // Definir campos de "Iniciar Sesión" dentro de Login y registro
    // Orden: login.action primeiro, depois os demais
    const camposIniciarSesion = ['login.action', 'login.title', 'login.usuario', 'login.password', 'login.default.action', 'login.forgotPassword.link', 'login.default.registro'];
    
    // Definir campos de "Recuperar Contraseña" dentro de Login y registro
    const camposRecuperarContrasena = [
      'login.forgotPassword.title',
      'login.forgotPassword.link',
      'login.forgotPassword.hint',
      'login.forgotPassword.label',
      'login.forgotPassword.action'
    ];
    
    // Definir campos de "Cambiar Contraseña" dentro de Login y registro
    const camposCambiarContrasena = ['login.changePassword.title', 'login.changePassword.hint', 'login.changePassword.newpassword', 'login.changePassword.newpasswordcopy', 'login.changePassword.action'];

    // Definir campos de "Registrar nuevo usuario" (título fora da subcategoria e subseções)
    const campoRegistrarTitulo = ['login.register.title'];
    const camposRegistrarDatos = [
      'login.register.fases.FASE1',
      'login.register.fields.nombre',
      'login.register.fields.razonsocial',
      'login.register.fields.nombrecomercial',
      'login.register.fields.pais',
      'login.register.fields.localidad',
      'login.register.fields.tipoDocumento',
      'login.register.fields.email',
      'login.register.fields.password',
      'login.register.fields.apellidos',
      'login.register.fields.direccion',
      'login.register.fields.nombreresponsable',
      'login.register.fields.provincia',
      'login.register.fields.codigoPostal',
      'login.register.fields.documento',
      'login.register.fields.emailconfirmacion',
      'login.register.fields.passwordconfirmacion',
      'login.register.actions.bill'
    ];
    const camposRegistrarMercado = [
      'login.register.fases.FASE2',
      'login.register.fields.mercado',
      'login.register.fields.procedencia'
    ];
    const camposRegistrarAdicionales = [
      'login.register.fases.FASE3',
      'login.register.fields.cargo',
      'login.register.fields.turismo',
      'login.register.fields.operador',
      'login.register.fields.idiomaAdicional'
    ];
    const camposRegistrarProteccion = [
      'login.register.fases.informacion',
      'login.register.fields.textoLegal',
      'login.register.fields.privacidad',
      'login.register.fields.publicidad'
    ];
    // Subcategoría de Pasos de la Compra (Checkout) -> Registros de visitas
    const camposRegistrosVisitas = [
      'step.date.title',
      'step.date.subtitle',
      'step.tickets.title',
      'step.tickets.subtitle',
      'step.hour.title',
      'step.hour.subtitle',
      'step.date.mainTitle',
      'step.date.state.available',
      'step.date.state.soldout',
      'step.date.state.last',
      'step.date.state.close',
      'step.date.calendarioEtiqueta_1',
      'step.date.calendarioEtiqueta_2',
      'step.navigation.next'
    ];
    const camposTiempo = [
      'timer.title'
    ];
    const camposNumeroEntradas = [
      'step.tickets.mainTitle',
      'step.navigation.previous'
    ];
    const camposHorarioVisita = [
      'step.hour.mainTitle'
    ];
    // Campos de Resumen del carrito
    const camposResumenCarrito = [
      'cart.resume.title',
      'cart.resume.subtitle',
      'cart.resume.card.actions.buyMore',
      'cart.resume.card.actions.edit',
      'cart.resume.card.actions.delete',
      'cart.resume.actions.next',
      'cart.resume.actions.more',
      'cart.resume.titleSuggest'
    ];
    // Campos de Formulario de compra - Datos del comprador
    const camposDatosComprador = [
      'data.banner',
      'data.title',
      'data.datosCliente',
      'data.fieldsClient.Nombrecontacto',
      'data.fieldsClient.nombre',
      'data.fieldsClient.apellido1',
      'data.fieldsClient.nombreresponsable',
      'data.fieldsClient.documento',
      'data.fieldsClient.emailconfirmacion',
      'data.fieldsClient.telefono',
      'data.fieldsClient.localidad',
      'data.fieldsClient.codigoPostal',
      'data.fieldsClient.nombrecentro',
      'data.fieldsClient.notas',
      'data.fieldsClient.notasinternas',
      'data.fieldsClient.nombreAdicional',
      'data.fieldsClient.apellidos',
      'data.fieldsClient.Apellido2Contacto',
      'data.fieldsClient.tipoDocumento',
      'data.fieldsClient.email',
      'data.fieldsClient.telefonomovil',
      'data.fieldsClient.direccion',
      'data.fieldsClient.provincia',
      'data.fieldsClient.pais',
      'data.fieldsClient.notasExternas'
    ];
    // Campos de Factura
    const camposFactura = [
      'data.bill'
    ];
    // Campos de Codigo Promocional
    const camposCodigoPromocional = [
      'data.codigoPromocionalTitle',
      'data.codigoPromocionalMsg',
      'data.codigoPromocionalAction'
    ];
    // Campos de Tarjeta Regalo
    const camposTarjetaRegalo = [
      'data.monedero.tarjetaMonedero',
      'data.monedero.monederoMsg',
      'data.monedero.action'
    ];
    // Campos de Medio de pago
    const camposMedioPago = [
      'data.seleccionaPago',
      'data.fieldsClient.textoLegal',
      'data.fieldsClient.privacidad',
      'data.fieldsClient.publicidad'
    ];
    // Campos de Página de gracias - Resumen de la compra
    const camposResumenCompra = [
      'pay.banner',
      'pay.title',
      'pay.textSend',
      'pay.actions.webExternal',
      'pay.share',
      'pay.actions.buy',
      'pay.linkInstagram'
    ];

    // Inicializar arrays para cada sección
    definicionSecciones.forEach(seccion => {
      seccionesMap.set(seccion.nombre, []);
    });

    // Clasificar cada traducción en su sección correspondiente
    Object.keys(translations).forEach(clave => {
      let seccionEncontrada = false;
      
      for (const seccion of definicionSecciones) {
        if (seccion.prefijos.length === 0) continue;
        
        if (seccion.prefijos.some(prefijo => clave.startsWith(prefijo))) {
          seccionesMap.get(seccion.nombre).push({
            clave: clave,
            valor: translations[clave],
            originalValor: translations[clave]
          });
          seccionEncontrada = true;
          break;
        }
      }

      // Si no encontró sección, se ignora (no se agrega a ninguna sección)
    });

    // Convertir el mapa a array de secciones
    this.secciones = definicionSecciones.map(seccionDef => {
      let traducciones = seccionesMap.get(seccionDef.nombre);
      
      // Para la sección Home, agregar separador "Home" al inicio y separador "Cookies" antes dos campos de cookies
      if (seccionDef.nombre === 'Página de inicio (Home)' && traducciones.length > 0) {
        // Separar campos gerais de Home dos campos de cookies
        const traduccionesHome: TraduccionItem[] = [];
        const traduccionesCookies: TraduccionItem[] = [];
        
        traducciones.forEach(t => {
          if (camposCookies.includes(t.clave)) {
            traduccionesCookies.push(t);
          } else {
            traduccionesHome.push(t);
          }
        });
        
        // Ordenar campos gerais de Home segundo a ordem definida
        traduccionesHome.sort((a, b) => {
          const indexA = camposHome.indexOf(a.clave);
          const indexB = camposHome.indexOf(b.clave);
          // Se não encontrar na lista, colocar no final
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
        
        // Criar array final com separadores
        const traduccionesFinal: TraduccionItem[] = [];
        
        // Agregar separador "Home" no início
        traduccionesFinal.push({
          clave: '___SEPARADOR_HOME___',
          valor: '',
          originalValor: '',
          esSeparador: true,
          nombreSubcategoria: 'Home'
        });
        
        // Agregar campos gerais de Home
        traduccionesFinal.push(...traduccionesHome);
        
        // Si hay campos de cookies, agregar separador y luego los campos
        if (traduccionesCookies.length > 0) {
          // Agregar separador "Cookies"
          traduccionesFinal.push({
            clave: '___SEPARADOR_COOKIES___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Configuración de privacidad'
          });
          
          // Agregar campos de cookies después del separador
          traduccionesFinal.push(...traduccionesCookies);
        }
        
        traducciones = traduccionesFinal;
      }
      
      // Para la sección Login y registro: Iniciar Sesión, Recuperar Contraseña, Cambiar Contraseña, y Registrar nuevo usuario
      if (seccionDef.nombre === 'Login y registro' && traducciones.length > 0) {
        // Separar campos por subcategorías
        const traduccionesLogin: TraduccionItem[] = [];
        const traduccionesIniciarSesion: TraduccionItem[] = [];
        const traduccionesRecuperarContrasena: TraduccionItem[] = [];
        const traduccionesCambiarContrasena: TraduccionItem[] = [];
        const traduccionesRegistrarTitulo: TraduccionItem[] = [];
        const traduccionesRegistrarDatos: TraduccionItem[] = [];
        const traduccionesRegistrarMercado: TraduccionItem[] = [];
        const traduccionesRegistrarAdicionales: TraduccionItem[] = [];
        const traduccionesRegistrarProteccion: TraduccionItem[] = [];
        const traduccionesOtros: TraduccionItem[] = [];
        
        // Lista de todas as chaves categorizadas para identificar campos não categorizados
        const todasChavesCategorizadas = [
          ...campoRegistrarTitulo,
          ...camposRegistrarDatos,
          ...camposRegistrarMercado,
          ...camposRegistrarAdicionales,
          ...camposRegistrarProteccion,
          ...camposIniciarSesion,
          ...camposRecuperarContrasena,
          ...camposCambiarContrasena
        ];
        
        traducciones.forEach(t => {
          // Permitir que campos apareçam em múltiplas subcategorias
          if (campoRegistrarTitulo.includes(t.clave)) {
            traduccionesRegistrarTitulo.push(t);
          }
          if (camposRegistrarDatos.includes(t.clave)) {
            traduccionesRegistrarDatos.push(t);
          }
          if (camposRegistrarMercado.includes(t.clave)) {
            traduccionesRegistrarMercado.push(t);
          }
          if (camposRegistrarAdicionales.includes(t.clave)) {
            traduccionesRegistrarAdicionales.push(t);
          }
          if (camposRegistrarProteccion.includes(t.clave)) {
            traduccionesRegistrarProteccion.push(t);
          }
          if (camposIniciarSesion.includes(t.clave)) {
            traduccionesIniciarSesion.push(t);
          }
          if (camposRecuperarContrasena.includes(t.clave)) {
            traduccionesRecuperarContrasena.push(t);
          }
          if (camposCambiarContrasena.includes(t.clave)) {
            traduccionesCambiarContrasena.push(t);
          }
          // Campos que não pertencem a nenhuma subcategoria específica -> irão para "Otros"
          if (!todasChavesCategorizadas.includes(t.clave)) {
            traduccionesOtros.push(t);
          }
        });
        
        // 1. Si hay campos de "Iniciar Sesión", agregar separador y luego los campos
        if (traduccionesIniciarSesion.length > 0) {
          traduccionesIniciarSesion.sort((a, b) => {
            const indexA = camposIniciarSesion.indexOf(a.clave);
            const indexB = camposIniciarSesion.indexOf(b.clave);
            return indexA - indexB;
          });
          
          traduccionesLogin.push({
            clave: '___SEPARADOR_INICIAR_SESION___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Iniciar Sesión'
          });
          
          traduccionesLogin.push(...traduccionesIniciarSesion);
        }
        
        // 2. Si hay campos de "Recuperar Contraseña", agregar separador y luego los campos
        if (traduccionesRecuperarContrasena.length > 0) {
          traduccionesRecuperarContrasena.sort((a, b) => {
            const indexA = camposRecuperarContrasena.indexOf(a.clave);
            const indexB = camposRecuperarContrasena.indexOf(b.clave);
            return indexA - indexB;
          });
          
          traduccionesLogin.push({
            clave: '___SEPARADOR_RECUPERAR_CONTRASENA___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Recuperar Contraseña'
          });
          
          traduccionesLogin.push(...traduccionesRecuperarContrasena);
        }
        
        // 3. Si hay campos de "Cambiar Contraseña", agregar separador y luego los campos
        if (traduccionesCambiarContrasena.length > 0) {
          traduccionesCambiarContrasena.sort((a, b) => {
            const indexA = camposCambiarContrasena.indexOf(a.clave);
            const indexB = camposCambiarContrasena.indexOf(b.clave);
            return indexA - indexB;
          });
          
          traduccionesLogin.push({
            clave: '___SEPARADOR_CAMBIAR_CONTRASENA___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Cambiar Contraseña'
          });
          
          traduccionesLogin.push(...traduccionesCambiarContrasena);
        }
        
        // 4. Estrutura: Separador "REGISTRAR NUEVO USUARIO" -> login.register.title -> Separador "DATOS DEL USUARIO" -> campos
        if (traduccionesRegistrarDatos.length > 0 || traduccionesRegistrarTitulo.length > 0) {
          // Ordenar campos de registro
          traduccionesRegistrarDatos.sort((a, b) => {
            const ia = camposRegistrarDatos.indexOf(a.clave);
            const ib = camposRegistrarDatos.indexOf(b.clave);
            return ia - ib;
          });
          traduccionesRegistrarMercado.sort((a, b) => {
            const ia = camposRegistrarMercado.indexOf(a.clave);
            const ib = camposRegistrarMercado.indexOf(b.clave);
            return ia - ib;
          });
          traduccionesRegistrarAdicionales.sort((a, b) => {
            const ia = camposRegistrarAdicionales.indexOf(a.clave);
            const ib = camposRegistrarAdicionales.indexOf(b.clave);
            return ia - ib;
          });
          traduccionesRegistrarProteccion.sort((a, b) => {
            const ia = camposRegistrarProteccion.indexOf(a.clave);
            const ib = camposRegistrarProteccion.indexOf(b.clave);
            return ia - ib;
          });
          
          traduccionesRegistrarTitulo.sort((a, b) => {
            const ia = campoRegistrarTitulo.indexOf(a.clave);
            const ib = campoRegistrarTitulo.indexOf(b.clave);
            return ia - ib;
          });
          
          // Separador "REGISTRAR NUEVO USUARIO" (categoria principal)
          traduccionesLogin.push({
            clave: '___SEPARADOR_REGISTRAR___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Registrar nuevo usuario',
            esSubcategoriaPrincipal: true // Flag para estilo principal
          });
          
          // login.register.title (no meio, fora da subcategoria)
          if (traduccionesRegistrarTitulo.length > 0) {
            traduccionesLogin.push(...traduccionesRegistrarTitulo);
          }
          
          // Separador "DATOS DEL USUARIO" (subcategoria aninhada dentro de "Registrar nuevo usuario")
          if (traduccionesRegistrarDatos.length > 0) {
            traduccionesLogin.push({
              clave: '___SEPARADOR_DATOS_USUARIO___',
              valor: '',
              originalValor: '',
              esSeparador: true,
              nombreSubcategoria: 'Datos del usuario',
              esSubcategoriaAninhada: true // Flag para estilo aninhado
            });
            
            // Campos de registro
            traduccionesLogin.push(...traduccionesRegistrarDatos);
          }

          // Separador "TIPO DE MERCADO Y PROCEDENCIA" (subcategoria aninhada dentro de "Registrar nuevo usuario")
          if (traduccionesRegistrarMercado.length > 0) {
            traduccionesLogin.push({
              clave: '___SEPARADOR_MERCADO_PROCEDENCIA___',
              valor: '',
              originalValor: '',
              esSeparador: true,
              nombreSubcategoria: 'Tipo de mercado y procedencia',
              esSubcategoriaAninhada: true // Flag para estilo aninhado
            });
            
            // Campos de mercado/procedencia
            traduccionesLogin.push(...traduccionesRegistrarMercado);
          }

          // Separador "DATOS ADICIONALES" (subcategoria aninhada dentro de "Registrar nuevo usuario")
          if (traduccionesRegistrarAdicionales.length > 0) {
            traduccionesLogin.push({
              clave: '___SEPARADOR_DATOS_ADICIONALES___',
              valor: '',
              originalValor: '',
              esSeparador: true,
              nombreSubcategoria: 'Datos adicionales',
              esSubcategoriaAninhada: true // Flag para estilo aninhado
            });
            
            // Campos de dados adicionais
            traduccionesLogin.push(...traduccionesRegistrarAdicionales);
          }

          // Separador "INFORMACIÓN DE PROTECCIÓN DE DATOS" (subcategoria aninhada dentro de "Registrar nuevo usuario")
          if (traduccionesRegistrarProteccion.length > 0) {
            traduccionesLogin.push({
              clave: '___SEPARADOR_PROTECCION_DATOS___',
              valor: '',
              originalValor: '',
              esSeparador: true,
              nombreSubcategoria: 'Información de protección de datos',
              esSubcategoriaAninhada: true // Flag para estilo aninhado
            });
            
            // Campos de proteção de dados
            traduccionesLogin.push(...traduccionesRegistrarProteccion);
          }
        }
        
        // Agregar subcategoria "Otros" al final si hay campos no categorizados
        if (traduccionesOtros.length > 0) {
          // Ordenar alfabéticamente por clave
          traduccionesOtros.sort((a, b) => a.clave.localeCompare(b.clave));
          
          traduccionesLogin.push({
            clave: '___SEPARADOR_OTROS___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Otros'
          });
          
          traduccionesLogin.push(...traduccionesOtros);
        }
        
        traducciones = traduccionesLogin;
      }

      // Para la sección Pasos de la Compra (Checkout): subcategoría "Registros de visitas", "Tiempo", "Número de entradas" y "Horario de visita"
      if (seccionDef.nombre === 'Pasos de la Compra (Checkout)' && traducciones.length > 0) {
        const traduccionesCheckout: TraduccionItem[] = [];
        const traduccionesRegistros: TraduccionItem[] = [];
        const traduccionesTiempo: TraduccionItem[] = [];
        const traduccionesNumeroEntradas: TraduccionItem[] = [];
        const traduccionesHorarioVisita: TraduccionItem[] = [];
        const traduccionesOtros: TraduccionItem[] = [];

        const todasChavesCategorizadas = [
          ...camposRegistrosVisitas,
          ...camposTiempo,
          ...camposNumeroEntradas,
          ...camposHorarioVisita
        ];

        traducciones.forEach(t => {
          if (camposRegistrosVisitas.includes(t.clave)) {
            traduccionesRegistros.push(t);
          } else if (camposTiempo.includes(t.clave)) {
            traduccionesTiempo.push(t);
          } else if (camposNumeroEntradas.includes(t.clave)) {
            traduccionesNumeroEntradas.push(t);
          } else if (camposHorarioVisita.includes(t.clave)) {
            traduccionesHorarioVisita.push(t);
          } else if (!todasChavesCategorizadas.includes(t.clave)) {
            // Campos no categorizados -> irán a "Otros"
            traduccionesOtros.push(t);
          }
        });

        // Ordenar según la lista declarada
        traduccionesRegistros.sort((a, b) => {
          const ia = camposRegistrosVisitas.indexOf(a.clave);
          const ib = camposRegistrosVisitas.indexOf(b.clave);
          return ia - ib;
        });

        traduccionesTiempo.sort((a, b) => {
          const ia = camposTiempo.indexOf(a.clave);
          const ib = camposTiempo.indexOf(b.clave);
          return ia - ib;
        });

        traduccionesNumeroEntradas.sort((a, b) => {
          const ia = camposNumeroEntradas.indexOf(a.clave);
          const ib = camposNumeroEntradas.indexOf(b.clave);
          return ia - ib;
        });

        traduccionesHorarioVisita.sort((a, b) => {
          const ia = camposHorarioVisita.indexOf(a.clave);
          const ib = camposHorarioVisita.indexOf(b.clave);
          return ia - ib;
        });

        if (traduccionesRegistros.length > 0) {
          traduccionesCheckout.push({
            clave: '___SEPARADOR_REGISTROS_VISITAS___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Registros de visitas'
          });
          traduccionesCheckout.push(...traduccionesRegistros);
        }

        // Subcategoria "Tiempo"
        if (traduccionesTiempo.length > 0) {
          traduccionesCheckout.push({
            clave: '___SEPARADOR_TIEMPO___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Tiempo'
          });
          traduccionesCheckout.push(...traduccionesTiempo);
        }

        // Subcategoria "Número de entradas"
        if (traduccionesNumeroEntradas.length > 0) {
          traduccionesCheckout.push({
            clave: '___SEPARADOR_NUMERO_ENTRADAS___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Número de entradas'
          });
          traduccionesCheckout.push(...traduccionesNumeroEntradas);
        }

        // Subcategoria "Horario de visita"
        if (traduccionesHorarioVisita.length > 0) {
          traduccionesCheckout.push({
            clave: '___SEPARADOR_HORARIO_VISITA___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Horario de visita'
          });
          traduccionesCheckout.push(...traduccionesHorarioVisita);
        }

        // Agregar subcategoria "Otros" al final si hay campos no categorizados
        if (traduccionesOtros.length > 0) {
          traduccionesOtros.sort((a, b) => a.clave.localeCompare(b.clave));
          
          traduccionesCheckout.push({
            clave: '___SEPARADOR_OTROS___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Otros'
          });
          traduccionesCheckout.push(...traduccionesOtros);
        }

        traducciones = traduccionesCheckout;
      }

      // Para la sección Resumen del carrito: label principal "Resumen del carrito"
      if (seccionDef.nombre === 'Resumen del carrito' && traducciones.length > 0) {
        const traduccionesResumen: TraduccionItem[] = [];
        const traduccionesResumenCarrito: TraduccionItem[] = [];
        const traduccionesOtros: TraduccionItem[] = [];

        traducciones.forEach(t => {
          if (camposResumenCarrito.includes(t.clave)) {
            traduccionesResumenCarrito.push(t);
          } else {
            traduccionesOtros.push(t);
          }
        });

        // Ordenar según la lista declarada
        traduccionesResumenCarrito.sort((a, b) => {
          const ia = camposResumenCarrito.indexOf(a.clave);
          const ib = camposResumenCarrito.indexOf(b.clave);
          return ia - ib;
        });

        // Label principal "Resumen del carrito"
        if (traduccionesResumenCarrito.length > 0) {
          traduccionesResumen.push({
            clave: '___SEPARADOR_RESUMEN_CARRITO___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Resumen del carrito'
          });
          traduccionesResumen.push(...traduccionesResumenCarrito);
        }

        // Subcategoria "Otros" sólo si hay campos sin categoría
        if (traduccionesOtros.length > 0) {
          traduccionesOtros.sort((a, b) => a.clave.localeCompare(b.clave));
          traduccionesResumen.push({
            clave: '___SEPARADOR_OTROS___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Otros'
          });
          traduccionesResumen.push(...traduccionesOtros);
        }

        traducciones = traduccionesResumen;
      }

      // Para la sección Formulario de compra: labels "Datos del comprador", "Factura", "Codigo Promocional", "Tarjeta Regalo" y "Medio de pago"
      if (seccionDef.nombre === 'Formulario de compra' && traducciones.length > 0) {
        const traduccionesFormulario: TraduccionItem[] = [];
        const traduccionesDatosComprador: TraduccionItem[] = [];
        const traduccionesFactura: TraduccionItem[] = [];
        const traduccionesCodigoPromocional: TraduccionItem[] = [];
        const traduccionesTarjetaRegalo: TraduccionItem[] = [];
        const traduccionesMedioPago: TraduccionItem[] = [];
        const traduccionesOtros: TraduccionItem[] = [];

        const todasChavesCategorizadas = [
          ...camposDatosComprador,
          ...camposFactura,
          ...camposCodigoPromocional,
          ...camposTarjetaRegalo,
          ...camposMedioPago
        ];

        traducciones.forEach(t => {
          if (camposDatosComprador.includes(t.clave)) {
            traduccionesDatosComprador.push(t);
          } else if (camposFactura.includes(t.clave)) {
            traduccionesFactura.push(t);
          } else if (camposCodigoPromocional.includes(t.clave)) {
            traduccionesCodigoPromocional.push(t);
          } else if (camposTarjetaRegalo.includes(t.clave)) {
            traduccionesTarjetaRegalo.push(t);
          } else if (camposMedioPago.includes(t.clave)) {
            traduccionesMedioPago.push(t);
          } else if (!todasChavesCategorizadas.includes(t.clave)) {
            traduccionesOtros.push(t);
          }
        });

        // Ordenar según la lista declarada
        traduccionesDatosComprador.sort((a, b) => {
          const ia = camposDatosComprador.indexOf(a.clave);
          const ib = camposDatosComprador.indexOf(b.clave);
          return ia - ib;
        });

        traduccionesFactura.sort((a, b) => {
          const ia = camposFactura.indexOf(a.clave);
          const ib = camposFactura.indexOf(b.clave);
          return ia - ib;
        });

        traduccionesCodigoPromocional.sort((a, b) => {
          const ia = camposCodigoPromocional.indexOf(a.clave);
          const ib = camposCodigoPromocional.indexOf(b.clave);
          return ia - ib;
        });

        traduccionesTarjetaRegalo.sort((a, b) => {
          const ia = camposTarjetaRegalo.indexOf(a.clave);
          const ib = camposTarjetaRegalo.indexOf(b.clave);
          return ia - ib;
        });

        traduccionesMedioPago.sort((a, b) => {
          const ia = camposMedioPago.indexOf(a.clave);
          const ib = camposMedioPago.indexOf(b.clave);
          return ia - ib;
        });

        // Label principal "Datos del comprador"
        if (traduccionesDatosComprador.length > 0) {
          traduccionesFormulario.push({
            clave: '___SEPARADOR_DATOS_COMPRADOR___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Datos del comprador'
          });
          traduccionesFormulario.push(...traduccionesDatosComprador);
        }

        // Label "Factura"
        if (traduccionesFactura.length > 0) {
          traduccionesFormulario.push({
            clave: '___SEPARADOR_FACTURA___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Factura'
          });
          traduccionesFormulario.push(...traduccionesFactura);
        }

        // Label "Codigo Promocional"
        if (traduccionesCodigoPromocional.length > 0) {
          traduccionesFormulario.push({
            clave: '___SEPARADOR_CODIGO_PROMOCIONAL___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Codigo Promocional'
          });
          traduccionesFormulario.push(...traduccionesCodigoPromocional);
        }

        // Label "Tarjeta Regalo"
        if (traduccionesTarjetaRegalo.length > 0) {
          traduccionesFormulario.push({
            clave: '___SEPARADOR_TARJETA_REGALO___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Tarjeta Regalo'
          });
          traduccionesFormulario.push(...traduccionesTarjetaRegalo);
        }

        // Label "Medio de pago"
        if (traduccionesMedioPago.length > 0) {
          traduccionesFormulario.push({
            clave: '___SEPARADOR_MEDIO_PAGO___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Medio de pago'
          });
          traduccionesFormulario.push(...traduccionesMedioPago);
        }

        // Subcategoria "Otros" sólo si hay campos sin categoría
        if (traduccionesOtros.length > 0) {
          traduccionesOtros.sort((a, b) => a.clave.localeCompare(b.clave));
          traduccionesFormulario.push({
            clave: '___SEPARADOR_OTROS___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Otros'
          });
          traduccionesFormulario.push(...traduccionesOtros);
        }

        traducciones = traduccionesFormulario;
      }

      // Para la sección Página de gracias (Thankyou): label "Resumen de la compra"
      if (seccionDef.nombre === 'Página de gracias (Thankyou)' && traducciones.length > 0) {
        const traduccionesThankyou: TraduccionItem[] = [];
        const traduccionesResumenCompra: TraduccionItem[] = [];
        const traduccionesOtros: TraduccionItem[] = [];

        traducciones.forEach(t => {
          if (camposResumenCompra.includes(t.clave)) {
            traduccionesResumenCompra.push(t);
          } else {
            traduccionesOtros.push(t);
          }
        });

        // Ordenar según la lista declarada
        traduccionesResumenCompra.sort((a, b) => {
          const ia = camposResumenCompra.indexOf(a.clave);
          const ib = camposResumenCompra.indexOf(b.clave);
          return ia - ib;
        });

        // Label "Resumen de la compra"
        if (traduccionesResumenCompra.length > 0) {
          traduccionesThankyou.push({
            clave: '___SEPARADOR_RESUMEN_COMPRA___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Resumen de la compra'
          });
          traduccionesThankyou.push(...traduccionesResumenCompra);
        }

        // Subcategoria "Otros" sólo si hay campos sin categoría
        if (traduccionesOtros.length > 0) {
          traduccionesOtros.sort((a, b) => a.clave.localeCompare(b.clave));
          traduccionesThankyou.push({
            clave: '___SEPARADOR_OTROS___',
            valor: '',
            originalValor: '',
            esSeparador: true,
            nombreSubcategoria: 'Otros'
          });
          traduccionesThankyou.push(...traduccionesOtros);
        }

        // Solo actualizar si hay traducciones organizadas, de lo contrario mantener las originales
        if (traduccionesThankyou.length > 0) {
          traducciones = traduccionesThankyou;
        }
      }
      
      const seccionFinal: SeccionTraducciones = {
        nombre: seccionDef.nombre,
        expandida: seccionDef.expandida,
        traducciones: traducciones,
        fotos: this.fotosPorSeccion.get(seccionDef.nombre) || []
      };
      
      // Inicializar array de fotos se não existir
      if (!this.fotosPorSeccion.has(seccionDef.nombre)) {
        this.fotosPorSeccion.set(seccionDef.nombre, []);
      }
      
      return seccionFinal;
    }).filter(seccion => seccion.traducciones.length > 0); // Solo mostrar secciones con contenido
    
    // console.log('[TraduccionesComponent] Secciones organizadas:', this.secciones.length);
    // console.log('[TraduccionesComponent] Array de secciones:', JSON.stringify(this.secciones.map(s => ({ nombre: s.nombre, count: s.traducciones.length }))));
    // this.secciones.forEach(seccion => {
    //   console.log(`  - ${seccion.nombre}: ${seccion.traducciones.length} traducciones`);
    // });
  }

  /**
   * Conta quantos campos uma subcategoria tem
   */
  contarCamposSubcategoria(traducciones: TraduccionItem[], nombreSubcategoria: string): number {
    let contador = 0;
    let dentroSubcategoria = false;
    let indiceInicio = -1;
    
    // Encontrar o índice do separador da subcategoria
    for (let i = 0; i < traducciones.length; i++) {
      const item = traducciones[i];
      if (item.esSeparador && item.nombreSubcategoria === nombreSubcategoria) {
        dentroSubcategoria = true;
        indiceInicio = i;
        break;
      }
    }
    
    if (!dentroSubcategoria || indiceInicio === -1) {
      return 0;
    }
    
    // Contar campos até encontrar o próximo separador (exceto subcategorias aninhadas)
    for (let i = indiceInicio + 1; i < traducciones.length; i++) {
      const item = traducciones[i];
      if (item.esSeparador) {
        // Se for uma subcategoria aninhada dentro da principal, continuar contando
        // Caso contrário, parar
        if (item.esSubcategoriaAninhada) {
          // Continuar contando campos da subcategoria aninhada
          continue;
        } else {
          // Encontrou outra subcategoria de mesmo nível, parar de contar
          break;
        }
      } else {
        contador++;
      }
    }
    
    return contador;
  }

  /**
   * Verifica se uma subcategoria deve ser colapsável
   * Agora todas as subcategorias são colapsáveis para manter consistência visual
   */
  deveSerColapsavel(traducciones: TraduccionItem[], nombreSubcategoria: string): boolean {
    // Todas as subcategorias são colapsáveis
    return true;
  }

  /**
   * Verifica se uma subcategoria é filha de outra (hierarquia)
   */
  obterSubcategoriaPai(nombreSubcategoria: string): string | null {
    // "Datos del usuario" é filha de "Registrar nuevo usuario"
    if (nombreSubcategoria === 'Datos del usuario') {
      return 'Registrar nuevo usuario';
    }
    // "Tipo de mercado y procedencia" também é filha de "Registrar nuevo usuario"
    if (nombreSubcategoria === 'Tipo de mercado y procedencia') {
      return 'Registrar nuevo usuario';
    }
    // "Datos adicionales" também é filha de "Registrar nuevo usuario"
    if (nombreSubcategoria === 'Datos adicionales') {
      return 'Registrar nuevo usuario';
    }
    // "Información de protección de datos" também é filha de "Registrar nuevo usuario"
    if (nombreSubcategoria === 'Información de protección de datos') {
      return 'Registrar nuevo usuario';
    }
    return null;
  }

  /**
   * Obtém todas as subcategorias filhas de uma subcategoria pai
   */
  obterSubcategoriasFilhas(nombreSubcategoriaPai: string): string[] {
    const filhas: string[] = [];
    // "Registrar nuevo usuario" tem "Datos del usuario" como filha
    if (nombreSubcategoriaPai === 'Registrar nuevo usuario') {
      filhas.push('Datos del usuario');
      filhas.push('Tipo de mercado y procedencia');
      filhas.push('Datos adicionales');
      filhas.push('Información de protección de datos');
    }
    return filhas;
  }

  /**
   * Verifica se uma subcategoria está expandida
   * Considera a hierarquia: se o pai estiver colapsado, a filha também estará
   * Por padrão, todas começam fechadas (colapsadas)
   */
  isSubcategoriaExpandida(nombreSubcategoria: string): boolean {
    // Verificar se tem um pai
    const pai = this.obterSubcategoriaPai(nombreSubcategoria);
    if (pai) {
      // Verificar o estado do pai (sem recursão para evitar loop)
      let estadoPai: boolean;
      if (!this.subcategoriasExpandidas.has(pai)) {
        estadoPai = false; // Por padrão fechado
      } else {
        estadoPai = this.subcategoriasExpandidas.get(pai) || false;
      }
      
      // Se o pai estiver colapsado, a filha também está
      if (!estadoPai) {
        return false;
      }
    }
    
    if (!this.subcategoriasExpandidas.has(nombreSubcategoria)) {
      // Por padrão, todas começam fechadas (colapsadas)
      this.subcategoriasExpandidas.set(nombreSubcategoria, false);
      return false;
    }
    return this.subcategoriasExpandidas.get(nombreSubcategoria) || false;
  }

  /**
   * Alterna o estado de expansão de uma subcategoria
   * Se for um pai, também colapsa suas filhas quando colapsado
   * Se for uma filha e o pai estiver colapsado, expande o pai automaticamente
   */
  toggleSubcategoria(nombreSubcategoria: string): void {
    // Verificar se é uma filha e o pai está colapsado
    const pai = this.obterSubcategoriaPai(nombreSubcategoria);
    if (pai) {
      // Verificar o estado atual do pai (sem usar isSubcategoriaExpandida para evitar recursão)
      let estadoPai: boolean;
      if (!this.subcategoriasExpandidas.has(pai)) {
        estadoPai = true; // Por padrão expandido
      } else {
        estadoPai = this.subcategoriasExpandidas.get(pai) || false;
      }
      
      // Se o pai estiver colapsado e o usuário quer expandir a filha, expandir o pai também
      if (!estadoPai) {
        this.subcategoriasExpandidas.set(pai, true);
      }
    }
    
    const atual = this.isSubcategoriaExpandida(nombreSubcategoria);
    const novoEstado = !atual;
    
    // Atualizar o estado desta subcategoria
    this.subcategoriasExpandidas.set(nombreSubcategoria, novoEstado);
    
    // Se for um pai e estiver sendo colapsado, colapsar também as filhas
    if (!novoEstado) {
      const filhas = this.obterSubcategoriasFilhas(nombreSubcategoria);
      filhas.forEach(filha => {
        this.subcategoriasExpandidas.set(filha, false);
      });
    }
  }

  /**
   * Verifica se um campo pertence a uma subcategoria específica
   */
  pertenceASubcategoria(traducciones: TraduccionItem[], index: number, nombreSubcategoria: string): boolean {
    if (index < 0 || index >= traducciones.length) return false;
    
    const item = traducciones[index];
    if (item.esSeparador) return false;
    
    // Procurar o separador da subcategoria antes deste campo
    let encontrouSeparador = false;
    let indiceSeparador = -1;
    
    for (let i = index - 1; i >= 0; i--) {
      const itemAnterior = traducciones[i];
      if (itemAnterior.esSeparador) {
        if (itemAnterior.nombreSubcategoria === nombreSubcategoria) {
          encontrouSeparador = true;
          indiceSeparador = i;
          break;
        } else {
          // Se encontrou outro separador antes, não pertence a esta subcategoria
          return false;
        }
      }
    }
    
    if (!encontrouSeparador) return false;
    
    // Verificar se não há outro separador entre o separador e este campo
    for (let i = indiceSeparador + 1; i < index; i++) {
      if (traducciones[i].esSeparador) {
        return false; // Encontrou outro separador entre a subcategoria e este campo
      }
    }
    
    return true;
  }

  /**
   * Verifica se um campo pertence a alguma subcategoria colapsável
   * Agora todas as subcategorias são colapsáveis
   */
  pertenceASubcategoriaColapsavel(traducciones: TraduccionItem[], index: number): boolean {
    // Encontrar todas as subcategorias (todas são colapsáveis agora)
    for (let i = 0; i < traducciones.length; i++) {
      const item = traducciones[i];
      if (item.esSeparador && item.nombreSubcategoria) {
        if (this.pertenceASubcategoria(traducciones, index, item.nombreSubcategoria)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Obtém o nome da subcategoria colapsável a que um campo pertence
   * Agora todas as subcategorias são colapsáveis
   */
  obterSubcategoriaColapsavel(traducciones: TraduccionItem[], index: number): string | null {
    // Encontrar todas as subcategorias (todas são colapsáveis agora)
    for (let i = 0; i < traducciones.length; i++) {
      const item = traducciones[i];
      if (item.esSeparador && item.nombreSubcategoria) {
        if (this.pertenceASubcategoria(traducciones, index, item.nombreSubcategoria)) {
          return item.nombreSubcategoria;
        }
      }
    }
    return null;
  }

  /**
   * Verifica se um campo pertence à subcategoria "Datos del usuario" (método legado, mantido para compatibilidade)
   */
  isCampoDatosUsuario(traducciones: TraduccionItem[], index: number): boolean {
    return this.pertenceASubcategoria(traducciones, index, 'Datos del usuario');
  }

  /**
   * Obtém todos os campos que não pertencem a nenhuma subcategoria
   */
  obterCamposSinSubcategoria(traducciones: TraduccionItem[]): TraduccionItem[] {
    const camposSinSubcategoria: TraduccionItem[] = [];
    
    for (let i = 0; i < traducciones.length; i++) {
      const item = traducciones[i];
      if (!item.esSeparador && !this.obterSubcategoriaColapsavel(traducciones, i)) {
        camposSinSubcategoria.push(item);
      }
    }
    
    return camposSinSubcategoria;
  }

  onTraduccionChange(traduccion: TraduccionItem) {
    // Normalizar valor: eliminar saltos de línea e espaços extras
    if (traduccion.valor !== undefined && traduccion.valor !== null) {
      const cleaned = traduccion.valor.toString().replace(/\n/g, "").trim();
      traduccion.valor = cleaned;
    }

    // Marcar que hay cambios
    this.hayCambios = this.secciones.some(seccion => 
      seccion.traducciones.some(t => t.valor !== t.originalValor)
    );
    this.panelService.setDiscardChanges(!this.hayCambios);
    this.panelService.setSaveActive(this.hayCambios);
  }

  // ═══════════════════════════════════════════════════
  // MÉTODOS PARA GESTIÓN DE FOTOS DE EVIDENCIA
  // ═══════════════════════════════════════════════════

  /**
   * Obtiene las fotos de una sección específica
   */
  getFotosSeccion(nombreSeccion: string): FotoEvidencia[] {
    return this.fotosPorSeccion.get(nombreSeccion) || [];
  }

  /**
   * Maneja la selección de archivos para upload
   */
  onFileSelected(event: any, nombreSeccion: string): void {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) return;

    Array.from(files).forEach(file => {
      // Validar que sea una imagen
      if (!file.type.startsWith('image/')) {
        this.snackvar_service.openSnackBar(
          `El archivo ${file.name} no es una imagen válida`,
          "",
          "red-snackbar"
        );
        return;
      }

      // Validar tamaño (máximo 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        this.snackvar_service.openSnackBar(
          `La imagen ${file.name} es demasiado grande. Máximo 5MB`,
          "",
          "red-snackbar"
        );
        return;
      }

      // Leer el archivo como Base64
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const foto: FotoEvidencia = {
          id: this.generateId(),
          nombre: file.name,
          url: e.target.result, // Base64
          file: file,
          fechaSubida: new Date()
        };

        // Agregar a la sección
        const fotos = this.fotosPorSeccion.get(nombreSeccion) || [];
        fotos.push(foto);
        this.fotosPorSeccion.set(nombreSeccion, fotos);

        // Actualizar la sección en el array
        const seccion = this.secciones.find(s => s.nombre === nombreSeccion);
        if (seccion) {
          seccion.fotos = fotos;
        }

        // Marcar cambios
        this.hayCambios = true;
        this.panelService.setDiscardChanges(false);
        this.panelService.setSaveActive(true);

        this.snackvar_service.openSnackBar(
          `Imagen ${file.name} agregada correctamente`,
          "",
          "green-snackbar"
        );
      };

      reader.onerror = () => {
        this.snackvar_service.openSnackBar(
          `Error al leer la imagen ${file.name}`,
          "",
          "red-snackbar"
        );
      };

      reader.readAsDataURL(file);
    });

    // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
    event.target.value = '';
  }

  /**
   * Elimina una foto de una sección
   */
  eliminarFoto(nombreSeccion: string, fotoId: string): void {
    const fotos = this.fotosPorSeccion.get(nombreSeccion) || [];
    const index = fotos.findIndex(f => f.id === fotoId);
    
    if (index !== -1) {
      fotos.splice(index, 1);
      this.fotosPorSeccion.set(nombreSeccion, fotos);

      // Actualizar la sección en el array
      const seccion = this.secciones.find(s => s.nombre === nombreSeccion);
      if (seccion) {
        seccion.fotos = fotos;
      }

      // Marcar cambios
      this.hayCambios = true;
      this.panelService.setDiscardChanges(false);
      this.panelService.setSaveActive(true);

      this.snackvar_service.openSnackBar(
        "Imagen eliminada correctamente",
        "",
        "green-snackbar"
      );
    }
  }

  /**
   * Abre uma imagem em um modal de preview
   */
  abrirPreview(foto: FotoEvidencia, seccionNombre?: string): void {
    // Se a guía visual está aberta, ocultar temporariamente para dar foco ao preview
    if (this.guiaVisible) {
      this.guiaWasOpen = true;
      this.guiaVisible = false;
    }

    // Determinar lista da seção para navegação
    const seccionOrigenNombre = seccionNombre || this.guiaSeccion?.nombre;
    if (seccionOrigenNombre) {
      this.previewLista = [...(this.getFotosSeccion(seccionOrigenNombre) || [])];
      const idx = this.previewLista.findIndex((f) => f.id === foto.id);
      this.previewIndex = idx >= 0 ? idx : 0;
    } else {
      this.previewLista = [foto];
      this.previewIndex = 0;
    }

    this.previewUrl = foto.url;
    this.previewNombre = foto.nombre;
    this.previewVisible = true;
    this.zoomLevel = 1;
    this.panX = 0;
    this.panY = 0;

    // Garantir que o modal fique visível no topo, evitando scroll manual
    setTimeout(() => {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        window.scrollTo(0, 0);
      }
    }, 0);
  }

  /**
   * Fecha o modal de preview
   */
  cerrarPreview(): void {
    this.previewVisible = false;
    this.previewUrl = "";
    this.previewNombre = "";
    this.zoomLevel = 1;
    this.panX = 0;
    this.panY = 0;
    this.previewLista = [];
    this.previewIndex = 0;

    // Restaurar a guía visual se estava aberta antes do preview
    if (this.guiaWasOpen) {
      this.guiaVisible = true;
      this.guiaWasOpen = false;
    }
  }

  /**
   * Genera un ID único para las fotos
   */
  private generateId(): string {
    return `foto_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Genera un ID único para el input de archivo basado en el nombre de la sección
   */
  generarIdInputFile(nombreSeccion: string): string {
    // Reemplazar caracteres no alfanuméricos por underscore
    const idSeguro = nombreSeccion.replace(/[^a-zA-Z0-9]/g, '_');
    return `fileInput_${idSeguro}`;
  }

  // ═══════════════════════════════════════════════════
  // CONTROLES DE ZOOM
  // ═══════════════════════════════════════════════════

  zoomIn(): void {
    this.setZoom(this.zoomLevel + this.zoomStep);
  }

  zoomOut(): void {
    this.setZoom(this.zoomLevel - this.zoomStep);
  }

  resetZoom(): void {
    this.zoomLevel = 1;
  }

  onWheelPreview(event: WheelEvent): void {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -this.zoomStep : this.zoomStep;
    this.setZoom(this.zoomLevel + delta);
  }

  private setZoom(value: number): void {
    const clamped = Math.max(this.zoomMin, Math.min(this.zoomMax, value));
    this.zoomLevel = parseFloat(clamped.toFixed(2));
  }

  // ═══════════════════════════════════════════════════
  // PAN (ARRASTAR PARA MOVER)
  // ═══════════════════════════════════════════════════

  startPan(event: PointerEvent): void {
    if (!this.previewVisible) return;
    event.preventDefault();
    this.isPanning = true;
    this.panStartClientX = event.clientX;
    this.panStartClientY = event.clientY;
    this.panStartX = this.panX;
    this.panStartY = this.panY;
    (event.target as HTMLElement)?.setPointerCapture?.(event.pointerId);
  }

  onPan(event: PointerEvent): void {
    if (!this.isPanning) return;
    event.preventDefault();
    const deltaX = event.clientX - this.panStartClientX;
    const deltaY = event.clientY - this.panStartClientY;
    this.panX = this.panStartX + deltaX;
    this.panY = this.panStartY + deltaY;
  }

  endPan(): void {
    this.isPanning = false;
  }

  // ═══════════════════════════════════════════════════
  // GUÍA VISUAL (MODAL DE GALERÍA)
  // ═══════════════════════════════════════════════════

  abrirGuiaVisual(seccion: SeccionTraducciones): void {
    this.guiaSeccion = seccion;
    this.guiaVisible = true;
  }

  cerrarGuiaVisual(): void {
    this.guiaVisible = false;
    this.guiaSeccion = null;
  }

  // ═══════════════════════════════════════════════════
  // NAVEGAÇÃO ENTRE FOTOS NO PREVIEW
  // ═══════════════════════════════════════════════════

  temAnterior(): boolean {
    return this.previewLista.length > 1 && this.previewIndex > 0;
  }

  temProximo(): boolean {
    return this.previewLista.length > 1 && this.previewIndex < this.previewLista.length - 1;
  }

  prevFoto(): void {
    if (!this.temAnterior()) return;
    this.previewIndex -= 1;
    const foto = this.previewLista[this.previewIndex];
    this.previewUrl = foto.url;
    this.previewNombre = foto.nombre;
  }

  nextFoto(): void {
    if (!this.temProximo()) return;
    this.previewIndex += 1;
    const foto = this.previewLista[this.previewIndex];
    this.previewUrl = foto.url;
    this.previewNombre = foto.nombre;
  }

  destroy() {
    // Cerrar el panel sin guardar (usado por el botón X)
    // console.log('[EditIdiomasComponent] Cerrando modal sin guardar');
    this.panelService.setDiscardChanges(true); // Indicar que no hay cambios pendientes
    this.panelService.setSaveActive(false); // Desactivar botón guardar
    this.panelService.setClosePanel(true); // Señal para cerrar el panel
  }

  save() {
    // Método auxiliar llamado después de guardar exitosamente
    this.loading = false;
    this.panelService.setDiscardChanges(true);
    this.panelService.setSavePanel(null);
  }

  discard() {
    // Restaurar valores originales (usado por el botón "Descartar cambios")
    // console.log('[EditIdiomasComponent] Descartando cambios');
    this.secciones.forEach(seccion => {
      seccion.traducciones.forEach(traduccion => {
        traduccion.valor = traduccion.originalValor;
      });
      // Restaurar fotos (por ahora, limpiar las fotos agregadas)
      // TODO: Implementar backup/restore de fotos cuando se carguen desde API
      if (seccion.fotos && seccion.fotos.length > 0) {
        // Por ahora, solo limpiar las fotos nuevas (no las originales)
        // En el futuro, aquí se restaurarían las fotos originales desde el backup
        seccion.fotos = [];
        this.fotosPorSeccion.set(seccion.nombre, []);
      }
    });
    this.hayCambios = false;
    this.panelService.setDiscardChanges(true);
    this.panelService.setSaveActive(false);
    // NO cerrar el modal, solo restaurar valores
  }

  submit() {
    // Guardar traducciones (usado por el botón "Guardar")
    // console.log('[EditIdiomasComponent] Guardando cambios');
    
    if (!this.codigoIdioma) {
      console.error('[EditIdiomasComponent] No hay código de idioma para guardar');
      this.snackvar_service.openSnackBar(
        this.translator.instant('editIdiomas.errorCodigoIdiomaNoDisponible') || 'No se puede guardar: código de idioma no disponible',
        "",
        "red-snackbar"
      );
      return;
    }
    
    // Preparar objeto de traducciones plano (clave-valor)
    const traduccionesPlanas: any = {};
    
    this.secciones.forEach(seccion => {
      seccion.traducciones.forEach(traduccion => {
        // Solo incluir campos que no son separadores
        if (!traduccion.esSeparador && traduccion.clave) {
          const valorNormalizado = this.normalizarValor(traduccion.valor);
          traduccion.valor = valorNormalizado; // reflejar no UI
          traduccionesPlanas[traduccion.clave] = valorNormalizado;
        }
      });
    });

    // console.log('[EditIdiomasComponent] Total de traducciones a guardar:', Object.keys(traduccionesPlanas).length);
    
    // Convertir objeto plano a estructura anidada (Contenido)
    const contenido = this.anidarObjeto(traduccionesPlanas);
    const mergedRequestBody = deepMerge({}, this.sourceTranslates, contenido);
    // console.log('[EditIdiomasComponent] Contenido anidado (primeras claves):', Object.keys(contenido).slice(0, 10));

    this.loading = true;

    // Llamar a la API para guardar
    this.idiomasService
      .insUpdtJsonRecursosIdiomas(this.codigoIdioma, mergedRequestBody)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (response) => {
          // console.log('[EditIdiomasComponent] Respuesta de guardado:', response);
          
          // Verificar si hay mensajes de error
          if (response?.Mensajes && response.Mensajes.length > 0) {
            // Hay errores, mostrarlos todos
            response.Mensajes.forEach((mensaje: any) => {
              this.snackvar_service.openSnackBar(
                mensaje.DescripcionMensaje || 'Error al guardar',
                "",
                "red-snackbar",
                mensaje.CodigoMensaje
              );
            });
            this.loading = false;
          } else if (response?.DatosResult?.id === 0 || (response?.Mensajes?.length === 0)) {
            // Éxito: cuando no hay mensajes de error, tratamos la respuesta como válida
            const successMsg = this.resolveMsg(
              'editIdiomas.datosActualizados',
              'Sus datos han sido actualizados en el cliente'
            );
            this.snackvar_service.openSnackBar(
              successMsg,
              "",
              "green-snackbar"
            );
            this.loading = false;
            this.hayCambios = false;
            this.save();
            this.destroy(); // Cerrar el modal después de guardar
          } else {
            // Respuesta inesperada
            console.warn('[EditIdiomasComponent] Respuesta inesperada:', response);
            const unexpectedMsg = this.resolveMsg(
              'editIdiomas.respuestaInesperada',
              'La operación se completó, pero la respuesta fue inesperada'
            );
            this.snackvar_service.openSnackBar(
              unexpectedMsg,
              "",
              "green-snackbar"
            );
            this.loading = false;
            this.hayCambios = false;
            this.save();
            this.destroy();
          }
        },
        (error) => {
          console.error('[EditIdiomasComponent] Error al guardar:', error);
          this.httpErrorService.identificarErrores(error);
          this.loading = false;
        }
      );
  }

  /**
   * Convierte un objeto plano con claves con puntos en un objeto anidado
   * Ejemplo: { "home.banner.title": "..." } -> { home: { banner: { title: "..." } } }
   */
  private anidarObjeto(obj: any): any {
    const result: any = {};
    
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const keys = key.split('.');
        let current = result;
        
        // Crear la estructura anidada
        for (let i = 0; i < keys.length - 1; i++) {
          const k = keys[i];
          if (!current[k]) {
            current[k] = {};
          }
          current = current[k];
        }
        
        // Asignar el valor en la última clave
        const lastKey = keys[keys.length - 1];
        current[lastKey] = obj[key];
      }
    }
    
    return result;
  }

  /**
   * Intenta obter texto traduzido; se a chave não existir, usa o fallback.
   */
  private resolveMsg(key: string, fallback: string): string {
    const translated = this.translator.instant(key);
    if (!translated || translated === key) {
      return fallback;
    }
    return translated;
  }

  /**
   * Normaliza un valor textual eliminando espacios al inicio y fin.
   * Si es null/undefined, devuelve cadena vacía.
   */
  private normalizarValor(valor: any): string {
    if (valor === null || valor === undefined) {
      return '';
    }
    if (typeof valor !== 'string') {
      return String(valor).trim();
    }
    return valor.trim();
  }
}
